import type React from "react";
import { tw } from "../../styling/helpers";
import {
	type FlexAlign,
	type FlexDirection,
	type FlexJustify,
	flexAlign,
	flexDirections,
	flexJustify,
	type Gap,
	gaps,
} from "../../styling/theme";

type Element =
	| "div"
	| "section"
	| "span"
	| "form"
	| "article"
	| "p"
	| "ul"
	| "li"
	| "aside"
	| "footer"
	| "header"
	| "dl";

type Props<T extends React.ElementType> = {
	gap?: Gap;
	component?: T;
	direction?: FlexDirection;
	justify?: FlexJustify;
	align?: FlexAlign;
	wrap?: boolean;
	fullSize?: boolean;
	children: React.ReactNode;
	grow?: boolean;
} & Omit<React.ComponentPropsWithoutRef<T>, "className">;

export const Stack = <T extends React.ElementType<any, Element> = "div">({
	gap = 0,
	component,
	direction = "col",
	justify = "start",
	align,
	children,
	fullSize = false,
	grow = true,
	wrap,
	...rest
}: Props<T>) => {
	const classes = tw(
		"flex",
		grow && "grow",
		flexDirections[direction],
		direction === "row" && "items-center",
		align && flexAlign[align],
		wrap && "flex-wrap",
		flexJustify[justify],
		fullSize && "size-full",
		gaps[gap],
	);

	const Component = component || "div";

	return (
		<Component className={classes} {...rest}>
			{children}
		</Component>
	);
};
